<template>
<div class="photo-selector" ref="photoSelector"
    :class="{'overflow-y-scroll': true}">
  <div v-if="uploadFile">
    <!-- 写真登録モード -->
    <div class="photo-add ta-center">
      <div class="canvas-wrapper">
        <canvas ref="previewCanvas"></canvas>
        <canvas v-show="false" ref="hiddenRealSizeCanvas"></canvas>
      </div>
    <div class="photo-add-bottom-bar ta-center">
        <button class="btn btn-lg btn-primary m4"
            :disabled="isUploadingPhoto"
            @click="addPhoto">
          <i class="fa fa-spinner fa-spin"
            v-show="isUploadingPhoto"></i>
          決定
        </button>
        <button class="btn btn-lg btn-secondary m4" @click="cancelAddPhoto">
          キャンセル
        </button>
      </div>
    </div>
  </div>
  <div v-if="isEditingPhoto">
    <!-- 編集モード -->
    <photo-editor
      :photo="selectedPhoto"
      @done="doneEditPhoto"
      @cancel="cancelEditPhoto">
    </photo-editor>
  </div>
  <div v-if="isNormalMode">
    <!-- 通常モード -->
    <div class="photo-list">
      <div class="thumbnail" v-for="(photo, index) in photos"
          :class="{
            selected: isSelectedPhoto(index),
            'used-photo': photo.is_used,
          }"
          :key="photo.id">
        <http-img class="photo-content"
          :src="photo.src"
          :is-blob-url="photo.isNew"
          @click="selectPhoto(photo, index)">
        </http-img>
      </div>
      <div class="btn-add-photo">
        <label class="btn btn-lg border-secondary text-secondary" for="photoFileInput">
          写真<br>登録
        </label>
        <input type="file" @change="fileSelected"
          accept=".jpg, .jpeg, .png"
          style="display:none;"
          id="photoFileInput"
          :disabled="showDeleteConfirm">
      </div>
      <div class="btn-add-photo">
        <label class="btn btn-lg border-secondary text-secondary"
        @click="showEmergencySelectorModal = true">
          緊急通報から<br>切り出し
        </label>
      </div>
    </div>
    <hr>
    <div class="selected-photo-area" v-if="selectedPhoto">
      <div class="mr8">
        <div>
          <http-img class="photo-img"
            :src="selectedPhoto.src"
            :is-blob-url="selectedPhoto.isNew">
          </http-img>
        </div>
        <div class="mt4">
          <http-link class="btn btn-sm border-secondary"
              :href="selectedPhoto.src"
              :is-blob-url="selectedPhoto.isNew"
              :filename="selectedPhoto.filename">
            <span class="text-secondary">
              <i class="fa fa-download"></i>
              ダウンロード
            </span>
          </http-link>
        </div>
      </div>
      <div>
        <div v-if="showDeleteConfirm">
          <!-- 削除確認モード -->
          <div class="mb10">
            <p>写真を削除します。よろしいですか？</p>
          </div>
          <div class="ta-center">
            <button class="btn btn-lg btn-primary m4"
                @click="removePhoto">
              OK
            </button>
            <button class="btn btn-lg btn-secondary m4"
                @click="closeShowDeleteConfirm">
              キャンセル
            </button>
          </div>
        </div>
        <div v-else>
          <button class="btn btn-lg btn-primary m4" @click="usePhoto"
              :disabled="selectedPhoto.is_used">
            <i class="fas fa-link"></i>
            決定
          </button>
          <button class="btn btn-lg btn-secondary m4" @click="editPhoto">
            <i class="fa fa-edit"></i>
            編集
          </button>
          <button class="btn btn-lg btn-danger m4"
              @click="showDeleteConfirm = true"
              v-show="!selectedPhoto.is_used">
            <i class="fas fa-trash-alt"></i>
            削除
          </button>
          <button class="btn btn-lg btn-secondary m4" @click="unusePhoto"
              v-show="selectedPhoto.is_used">
            <i class="fas fa-unlink"></i>
            解除
          </button>
        </div>
      </div>
    </div>
  </div>
  <modal v-show="showEmergencySelectorModal"
      class="photo-selector"
      :title="emergencySelectorModalTitle"
      :showTopDismissButton="true"
      topDismissButtonLabel="閉じる"
      topDismissButtonClass="top-dismiss btn border-secondary text-secondary"
      :showFooter="false"
      @dismiss="showEmergencySelectorModal = false">
    <div slot="body">
      <emergency-selector
        @selected-screenshot="onSelectedScreenshot">
      </emergency-selector>
    </div>
  </modal>
</div>
</template>

<script>
import imageMixin from 'src/mixins/imageMixin'
import mediaMixin from 'src/mixins/mediaMixin'
import notificationMixin from 'src/mixins/notificationMixin'
import PhotoEditor from 'src/components/PhotoEditor'
import EmergencySelector from 'src/components/EmergencySelector'

const MODE_NORMAL = 'normal'
const MODE_ADD = 'add'
const MODE_EDIT = 'edit'
const MODE_SCREENSHOT = 'screenshot'

export default {
  components: { PhotoEditor, EmergencySelector },
  name: 'photo-selector',
  props: {
    photos: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      uploadFile: null,
      selectedPhoto: null,
      isUploadingPhoto: false,
      isEditingPhoto: false,
      showDeleteConfirm: false,
      showEmergencySelectorModal: false,
      onResizeFunc: () => {},
      emergencySelectorModalTitle: '緊急通報写真切り出し',
      isConfirmingEmergencySelector: false,
    }
  },
  computed: {
    isNormalMode() {
      return !this.uploadFile && !this.isEditingPhoto
    },
  },
  watch: {
    uploadFile() {
      if (!this.uploadFile) { return }
      this.loadImageWithExif(this.uploadFile.src)
        .then(image => {
          const previewCanvas = this.$refs.previewCanvas
          const hiddenRealSizeCanvas = this.$refs.hiddenRealSizeCanvas
          const maxHeight = window.innerHeight - 280
          const maxWidth = this.$refs.photoSelector.offsetWidth
          this.writeImageToCanvas(image, previewCanvas,
            { maxHeight, maxWidth })
          this.writeImageToCanvas(image, hiddenRealSizeCanvas)
          this.uploadFile.exif = image.exifdata
        })
    },
  },
  mounted() {
    this.setListeners()
    if (this.photos.length > 0) {
      const index = 0
      this.selectPhoto(this.photos[index], index)
    }
  },
  destroyed() {
    window.removeEventListener('resize', this.onResizeFunc)
  },
  mixins: [imageMixin, mediaMixin, notificationMixin],
  methods: {
    setMaxHeight() {
      this.$refs.photoSelector.style.maxHeight =
        (window.innerHeight - (60 + 70 + 50)) + 'px'
    },
    setListeners() {
      this.onResizeFunc = () => {
        this.setMaxHeight()
      }
      window.addEventListener('resize', this.onResizeFunc)
      this.onResizeFunc()
    },
    emitModeTransition(mode) {
      let titleStr = ''
      if (mode === MODE_NORMAL) {
        titleStr = '写真選択'
      } else if (mode === MODE_ADD) {
        titleStr = '写真登録'
      } else if (mode === MODE_EDIT) {
        titleStr = '写真編集'
      } else if (mode === MODE_SCREENSHOT) {
        titleStr = '確認'
      }
      this.$emit('mode-change', {
        mode: mode,
        title: titleStr,
      })
    },
    selectPhoto(photo, index) {
      if (this.showDeleteConfirm) { return }
      let selectedPhoto = null
      if (!this.isSelectedPhoto(index)) {
        selectedPhoto = photo
        selectedPhoto.index = index
      }
      this.selectedPhoto = selectedPhoto
    },
    isSelectedPhoto(index) {
      if (!this.selectedPhoto) { return false }
      return index === this.selectedPhoto.index
    },
    fileSelected(e) {
      if (!e.target.files || e.target.files.length === 0) {
        return
      }
      const f = e.target.files[0]
      this.uploadFile = {
        filename: f.name,
        type: f.type,
        file: f,
        src: window.URL.createObjectURL(f),
      }
      this.emitModeTransition(MODE_ADD)
    },
    onSelectedScreenshot(params) {
      if (!params) {
        return
      }
      this.uploadFile = params
      this.isConfirmingEmergencySelector = true
      this.showEmergencySelectorModal = false
      this.emitModeTransition(MODE_SCREENSHOT)
    },
    cancelAddPhoto() {
      this.uploadFile = null
      if (this.isConfirmingEmergencySelector) {
        this.isConfirmingEmergencySelector = false
        this.showEmergencySelectorModal = true
      }
      this.emitModeTransition(MODE_NORMAL)
    },
    emitRefresh(photos) {
      this.$emit('should-refresh', photos)
    },
    async addPhoto() {
      const photos = this.photos.slice()
      const photo = this.uploadFile
      // photo.file = this.canvasToBlob(this.$refs.hiddenRealSizeCanvas)
      // 緊急通報から切り出した写真の場合は、事前にmetadata情報を設定してある
      if (!photo.metadata) {
        const geoInfo = this.getGeoInfoFromExif(photo.exif)
        const metadata = {
          datetime: geoInfo.datetime,
          latitude: geoInfo.latitude,
          longitude: geoInfo.longitude,
          geo_locations: geoInfo.geoLocations,
        }
        photo.metadata = metadata
      }
      photo.isNew = true
      photo.index = photos.length
      photos.splice(photo.index, 1, photo)

      this.selectedPhoto = photo
      this.uploadFile = null
      this.emitModeTransition(MODE_NORMAL)
      this.emitRefresh(photos)
      this.usePhoto()
    },
    async usePhoto() {
      if (!this.selectedPhoto) { return }

      const photos = this.photos.slice()
      const photo = this.selectedPhoto
      photo.is_used = true
      photo.disp_order = Math.max(...photos.map(e => e.disp_order), 0) + 1
      photos.splice(photo.index, 1, photo)

      this.selectedPhoto = photo
      this.emitRefresh(photos)
      this.$emit('use-photo', this.selectedPhoto)
    },
    async unusePhoto() {
      if (!this.selectedPhoto) { return }

      const photos = this.photos.slice()
      const photo = this.selectedPhoto
      photo.is_used = false
      photo.disp_order = null
      photos.splice(photo.index, 1, photo)

      this.selectedPhoto = photo
      this.emitRefresh(photos)
      this.$emit('unuse-photo', this.selectedPhoto)
    },
    editPhoto() {
      if (!this.selectedPhoto) { return }
      this.isEditingPhoto = true
      this.emitModeTransition(MODE_EDIT)
    },
    doneEditPhoto(photo) {
      const photos = this.photos.slice()
      const origPhoto = this.selectedPhoto
      photo.isNew = true
      photo.index = photos.length
      photo.is_used = origPhoto.is_used
      photo.disp_order = origPhoto.disp_order
      photo.metadata = origPhoto.metadata
      photos.splice(photo.index, 1, photo)
      origPhoto.is_used = false
      origPhoto.disp_order = null
      photos.splice(origPhoto.index, 1, origPhoto)

      this.selectedPhoto = photo
      this.isEditingPhoto = false
      this.emitModeTransition(MODE_NORMAL)
      this.emitRefresh(photos)
    },
    cancelEditPhoto() {
      this.isEditingPhoto = false
      this.emitModeTransition(MODE_NORMAL)
    },
    closeShowDeleteConfirm() {
      this.showDeleteConfirm = false
    },
    async removePhoto() {
      if (!this.selectedPhoto) { return }

      const photos = this.photos.slice()
      const photo = this.selectedPhoto
      photos.splice(photo.index, 1)

      this.selectedPhoto = null
      this.showDeleteConfirm = false
      this.emitRefresh(photos)
    },
  },
}
</script>

<style lang="scss" scoped>
.overflow-y-scroll {
  overflow-y: scroll;
}
.photo-list {
  width: 100%;
  height: 100%;
  margin: 0;
  display: flex;
  flex-wrap: wrap;
}
.thumbnail{
  width: 100px;
  height: 100px;
  padding: 4px;
  margin: 4px 8px;
  cursor: pointer;
  border: 1px solid #DFD7CA;
  border-radius: 0.25rem;
  &.selected {
    border-color: red;
  }
  &.used-photo {
    opacity: 0.5;
    cursor: default;
  }
  .photo-content {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}
.btn-add-photo {
  float: left;
  margin: 4px 8px;
}
.selected-photo-area {
  display: flex;
}
.photo-img {
  max-height: 300px;
  max-width: 100%;
}
.photo-add-bottom-bar {
  margin-top: 8px;
}
</style>
